// StudentCheckIn.jsx
// Pure UI — no stream management, no MediaPipe.
// Receives proctoring state as props, renders accordingly.
// Merged with CheckInWrapper — owns its own full-screen centering layout.
//
// Props:
//   proctoring  — the full return value of useProctoring()
//   onStart     — called with { name, className, roll } when student clicks Start

import { useState, useEffect, useCallback } from "react";
import { RocketIcon, CameraIcon, CameraOffIcon, UserIcon, HashIcon, BookOpenIcon } from "./Utilities";

// ── useIsMobile ──────────────────────────────────────────────────────────────

function useIsMobile(breakpoint = 600) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

// ── Inline SVG icons ─────────────────────────────────────────────────────────

function MicIcon({ size = 18, color = "#666" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function MicOffIcon({ size = 18, color = "#f87171" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .6-.08 1.19-.22 1.74" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function CheckCircleIcon({ size = 14, color = "#16a34a" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function StudentCheckIn({ proctoring, onStart }) {
  const isMobile = useIsMobile();

  const [fields, setFields] = useState({ name: "", className: "", roll: "" });
  const [errors, setErrors] = useState({});
  const [starting, setStarting] = useState(false);

  // ── Derived states ──
  const {
    status,
    videoRef,
    retry,
    calibrationState,
    calibrationProgress,
    calibrationError,
    calibrationData,
    startCalibration,
  } = proctoring;

  const hasCam = status === "active";
  const hasMic = status === "active"; // single getUserMedia covers both
  const denied = status === "denied";
  const errored = status === "error";
  const pending = status === "requesting";

  // ── Sync video srcObject when element mounts ──
  // useProctoring may have acquired the stream before this component renders.
  // We need to attach srcObject once the <video> element mounts.
  const setVideoElement = useCallback(
    (el) => {
      // Point useProctoring's videoRef at this DOM element
      if (videoRef) videoRef.current = el;

      // If stream is already live, attach it
      if (el && proctoring._streamRef?.current) {
        el.srcObject = proctoring._streamRef.current;
        el.play().catch(() => {});
      }
    },
    [videoRef, proctoring._streamRef]
  );

  // ── Form handlers ──
  const handleChange = (key) => (e) => {
    setFields((f) => ({ ...f, [key]: e.target.value }));
    setErrors((err) => ({ ...err, [key]: false }));
  };

  const allFilled = fields.name.trim() && fields.className.trim() && fields.roll.trim();

  const handleStart = () => {
    const newErrors = {};
    if (!fields.name.trim()) newErrors.name = true;
    if (!fields.className.trim()) newErrors.className = true;
    if (!fields.roll.trim()) newErrors.roll = true;
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    setStarting(true);
    setTimeout(() => {
      const result = onStart?.({
        name: fields.name.trim(),
        className: fields.className.trim(),
        roll: fields.roll.trim(),
      });
      if (result === false) setStarting(false);
    }, 400);
  };

  // ── Calibration button label ──
  const calBtnLabel = (() => {
    if (calibrationState === "done") return "✓ Calibration Complete";
    if (calibrationState === "running") return "Calibrating…";
    if (calibrationState === "failed") return "Retry Calibration";
    return "Start Calibration";
  })();

  // ═════════════════════════════════════════════════════════════════════════
  // PERMISSION STATUS PILLS
  // ═════════════════════════════════════════════════════════════════════════

  const PermissionPills = (
    <div style={s.permissionRow}>
      {/* Camera */}
      <div style={{
        ...s.permPill,
        ...(hasCam ? s.permPillOk : denied ? s.permPillDenied : s.permPillWaiting),
      }}>
        {hasCam
          ? <><CheckCircleIcon size={13} color="#16a34a" /> Camera</>
          : denied
            ? <><CameraOffIcon size={13} color="#DC2626" /> Blocked</>
            : <><CameraIcon size={13} color="#9CA3AF" /> Waiting…</>
        }
      </div>

      {/* Microphone */}
      <div style={{
        ...s.permPill,
        ...(hasMic ? s.permPillOk : denied ? s.permPillDenied : s.permPillWaiting),
      }}>
        {hasMic
          ? <><CheckCircleIcon size={13} color="#16a34a" /> Microphone</>
          : denied
            ? <><MicOffIcon size={13} color="#DC2626" /> Blocked</>
            : <><MicIcon size={13} color="#9CA3AF" /> Waiting…</>
        }
      </div>
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // CAMERA PANEL
  // ═════════════════════════════════════════════════════════════════════════

  const CameraPanel = (
    <div style={isMobile ? s.camPanelMobile : s.camPanel}>
      <div style={s.camLabel}>Your Camera</div>

      {/* Video / placeholder */}
      <div style={isMobile ? s.camBoxMobile : s.camBox}>
        {hasCam ? (
          <video ref={setVideoElement} autoPlay playsInline muted style={s.video} />
        ) : (
          <div style={s.camPlaceholder}>
            {pending && (
              <div style={s.camHint}>
                <CameraIcon size={28} color="#ccc" />
                <span style={s.camHintText}>Requesting access…</span>
              </div>
            )}
            {denied && (
              <div style={s.camHint}>
                <CameraOffIcon size={28} color="#f87171" />
                <span style={{ ...s.camHintText, color: "#f87171" }}>
                  Camera & mic blocked
                </span>
              </div>
            )}
            {errored && (
              <div style={s.camHint}>
                <CameraOffIcon size={28} color="#f87171" />
                <span style={{ ...s.camHintText, color: "#f87171" }}>
                  Device not available
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Permission pills */}
      {PermissionPills}

      {/* Retry button — only if denied or errored */}
      {(denied || errored) && (
        <button style={s.retryBtn} onClick={retry}>
          Allow Camera & Microphone
        </button>
      )}

      {/* Status line */}
      {hasCam && calibrationState === "idle" && (
        <div style={s.statusLine}>
          <span style={s.dot} /> Camera on — ready to calibrate
        </div>
      )}
      {hasCam && calibrationState === "done" && (
        <div style={{ ...s.statusLine, color: "#16a34a" }}>
          <span style={{ ...s.dot, background: "#16a34a", boxShadow: "0 0 6px #16a34a" }} />
          Calibration done · {calibrationData?.frameCount} frames
        </div>
      )}

      {/* Calibration button */}
      {hasCam && (
        <button
          style={{
            ...s.calBtn,
            ...(calibrationState === "done" ? s.calBtnDone : {}),
            ...(calibrationState === "running" ? s.calBtnRunning : {}),
          }}
          onClick={
            calibrationState === "done" || calibrationState === "running"
              ? undefined
              : startCalibration
          }
          disabled={calibrationState === "running" || calibrationState === "done"}
        >
          {calBtnLabel}
        </button>
      )}

      {/* Calibration progress bar */}
      {hasCam && calibrationState === "running" && (
        <div style={s.calBox}>
          <div style={s.calInstruction}>
            👁 Look straight at the camera and hold still
          </div>
          <div style={s.calBarTrack}>
            <div style={{ ...s.calBarFill, width: `${calibrationProgress}%` }} />
          </div>
          <div style={s.calPct}>{calibrationProgress}%</div>
        </div>
      )}

      {/* Calibration error */}
      {calibrationState === "failed" && calibrationError && (
        <div style={s.calErrorBox}>
          <span style={s.calErrorText}>{calibrationError}</span>
        </div>
      )}

      {/* Pre-calibration hint */}
      {hasCam && calibrationState === "idle" && (
        <div style={s.calHint}>
          Position your face in the camera, then click{" "}
          <strong>Start Calibration</strong> and look straight ahead for 5 seconds.
        </div>
      )}
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // FORM PANEL
  // ═════════════════════════════════════════════════════════════════════════

  const FormPanel = (
    <div style={isMobile ? s.formPanelMobile : s.formPanel}>
      <div style={s.formTitle}>Student Details</div>
      <div style={s.formSubtitle}>Fill in your info to begin</div>

      <div style={s.fields}>
        <InputField
          icon={<UserIcon color={errors.name ? "#f87171" : "#bbb"} />}
          placeholder="Full Name"
          value={fields.name}
          onChange={handleChange("name")}
          error={errors.name}
          autoFocus={!isMobile}
        />
        <InputField
          icon={<BookOpenIcon color={errors.className ? "#f87171" : "#bbb"} />}
          placeholder="Class / Section"
          value={fields.className}
          onChange={handleChange("className")}
          error={errors.className}
        />
        <InputField
          icon={<HashIcon color={errors.roll ? "#f87171" : "#bbb"} />}
          placeholder="Roll Number"
          value={fields.roll}
          onChange={handleChange("roll")}
          error={errors.roll}
          type="text"
          inputMode="numeric"
        />
      </div>

      <button
        style={{
          ...s.startBtn,
          ...(allFilled ? s.startBtnActive : {}),
          transform: starting ? "scale(0.96)" : "scale(1)",
        }}
        onClick={handleStart}
        disabled={starting}
      >
        <RocketIcon />
        {starting ? "Launching…" : "Start Homework"}
      </button>

      {Object.keys(errors).length > 0 && (
        <div style={s.errorMsg}>Please fill all fields above ↑</div>
      )}

      {/* Nudge: camera on but not calibrated */}
      {hasCam && calibrationState !== "done" && allFilled && (
        <div style={s.calNudge}>
          💡 Tip: Run calibration first for starting the test.
        </div>
      )}

      {/* Nudge: permissions not yet granted */}
      {!hasCam && !pending && allFilled && (
        <div style={s.calNudge}>
          ⚠️ Camera & microphone access is needed before you can start.
        </div>
      )}
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // LAYOUT — full-screen centered card (merged wrapper)
  // ═════════════════════════════════════════════════════════════════════════

  return (
    <div style={s.outerWrap}>
      <div style={s.card}>
        {isMobile ? (
          <div style={s.rootMobile}>
            {CameraPanel}
            <div style={s.mobileDivider} />
            {FormPanel}
          </div>
        ) : (
          <div style={s.rootDesktop}>
            {CameraPanel}
            <div style={s.desktopDivider} />
            {FormPanel}
          </div>
        )}
      </div>
    </div>
  );
}

// ── InputField ───────────────────────────────────────────────────────────────

function InputField({ icon, placeholder, value, onChange, error, type = "text", inputMode, autoFocus }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{
      ...s.inputWrap,
      borderColor: error ? "#f87171" : focused ? "#FBBF24" : "#E5E7EB",
      boxShadow: focused ? "0 0 0 3px rgba(251,191,36,0.18)" : "none",
    }}>
      <span style={s.inputIcon}>{icon}</span>
      <input
        type={type}
        inputMode={inputMode}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        autoFocus={autoFocus}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={s.input}
      />
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  // ── Outer wrapper (merged from CheckInWrapper) ──
  outerWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100vw",
    height: "100vh",
    background: "#f8f7ff",
  },
  card: {
    width: "min(94vw, 960px)",
    maxHeight: "94vh",
    overflow: "auto",
    borderRadius: 24,
    background: "#ffffff",
    boxShadow: "0 0 0 1px rgba(0,0,0,0.03), 0 12px 48px rgba(0,0,0,0.08)",
    padding: 16,
  },

  // ── Layout roots ──
  rootDesktop: {
    display: "flex",
    flexDirection: "row",
    gap: "24px",
    alignItems: "stretch",
    width: "100%",
    height: "100%",
  },
  rootMobile: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    width: "100%",
  },

  // ── Camera panel ──
  camPanel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    flex: "1 1 0",
    minWidth: 0,
  },
  camPanelMobile: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    width: "100%",
  },
  camLabel: {
    fontFamily: "'Nunito', sans-serif",
    fontWeight: 700,
    fontSize: "11px",
    color: "#999",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    alignSelf: "flex-start",
  },
  camBox: {
    width: "100%",
    aspectRatio: "4/3",
    borderRadius: "14px",
    overflow: "hidden",
    background: "#F3F4F6",
    border: "2px solid #E5E7EB",
    flexShrink: 0,
  },
  camBoxMobile: {
    width: "100%",
    aspectRatio: "16/9",
    borderRadius: "12px",
    overflow: "hidden",
    background: "#F3F4F6",
    border: "2px solid #E5E7EB",
    flexShrink: 0,
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transform: "scaleX(-1)",
    display: "block",
  },
  camPlaceholder: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  camHint: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
  },
  camHintText: {
    fontFamily: "'Nunito', sans-serif",
    fontSize: "11px",
    color: "#bbb",
    fontWeight: 600,
    textAlign: "center",
  },

  // ── Permission pills ──
  permissionRow: {
    display: "flex",
    gap: "8px",
    width: "100%",
    justifyContent: "center",
  },
  permPill: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontFamily: "'Nunito', sans-serif",
    fontWeight: 700,
  },
  permPillOk: {
    background: "#F0FDF4",
    color: "#16a34a",
    border: "1px solid #BBF7D0",
  },
  permPillDenied: {
    background: "#FEF2F2",
    color: "#DC2626",
    border: "1px solid #FECACA",
  },
  permPillWaiting: {
    background: "#F9FAFB",
    color: "#9CA3AF",
    border: "1px solid #E5E7EB",
  },

  retryBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    background: "#374151",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    padding: "8px 16px",
    fontSize: "12px",
    fontWeight: 700,
    fontFamily: "'Nunito', sans-serif",
    cursor: "pointer",
    width: "100%",
    transition: "background 0.25s",
  },

  // ── Status line ──
  statusLine: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontFamily: "'Nunito', sans-serif",
    fontSize: "12px",
    fontWeight: 700,
    color: "#22c55e",
  },
  dot: {
    display: "inline-block",
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    background: "#22c55e",
    boxShadow: "0 0 6px #22c55e",
  },

  // ── Calibration ──
  calBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    background: "linear-gradient(135deg,#FBBF24 0%,#F59E0B 100%)",
    color: "#7c2d00",
    border: "none",
    borderRadius: "10px",
    padding: "8px 16px",
    fontSize: "12px",
    fontWeight: 700,
    fontFamily: "'Nunito', sans-serif",
    cursor: "pointer",
    width: "100%",
    transition: "background 0.25s, opacity 0.2s",
  },
  calBtnDone: {
    background: "#16a34a",
    color: "#fff",
    cursor: "default",
    opacity: 1,
  },
  calBtnRunning: {
    background: "#D1D5DB",
    color: "#6B7280",
    cursor: "wait",
  },
  calBox: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    alignItems: "center",
  },
  calInstruction: {
    fontFamily: "'Nunito', sans-serif",
    fontSize: "12px",
    fontWeight: 700,
    color: "#374151",
    textAlign: "center",
  },
  calBarTrack: {
    width: "100%",
    height: "6px",
    background: "#E5E7EB",
    borderRadius: "99px",
    overflow: "hidden",
  },
  calBarFill: {
    height: "100%",
    background: "linear-gradient(90deg, #FBBF24, #F59E0B)",
    borderRadius: "99px",
    transition: "width 0.1s linear",
  },
  calPct: {
    fontFamily: "'Nunito', sans-serif",
    fontSize: "11px",
    fontWeight: 700,
    color: "#F59E0B",
  },
  calHint: {
    fontFamily: "'Nunito', sans-serif",
    fontSize: "11px",
    fontWeight: 600,
    color: "#9CA3AF",
    textAlign: "center",
    lineHeight: 1.5,
  },
  calErrorBox: {
    background: "#FEF2F2",
    border: "1.5px solid #FECACA",
    borderRadius: "10px",
    padding: "8px 12px",
    width: "100%",
  },
  calErrorText: {
    fontFamily: "'Nunito', sans-serif",
    fontSize: "11px",
    fontWeight: 700,
    color: "#DC2626",
  },
  calNudge: {
    fontFamily: "'Nunito', sans-serif",
    fontSize: "11px",
    fontWeight: 600,
    color: "#9CA3AF",
    textAlign: "center",
    marginTop: "-4px",
  },

  // ── Dividers ──
  desktopDivider: {
    width: "1.5px",
    background: "#F0E8C8",
    borderRadius: "2px",
    alignSelf: "stretch",
    flexShrink: 0,
  },
  mobileDivider: {
    width: "100%",
    height: "1.5px",
    background: "#F0E8C8",
    borderRadius: "2px",
    flexShrink: 0,
  },

  // ── Form ──
  formPanel: {
    display: "flex",
    flexDirection: "column",
    gap: "11px",
    flex: "1 1 0",
    minWidth: 0,
    justifyContent: "center",
  },
  formPanelMobile: {
    display: "flex",
    flexDirection: "column",
    gap: "11px",
    width: "100%",
  },
  formTitle: {
    fontFamily: "'Playfair Display', serif",
    fontWeight: 900,
    fontSize: "20px",
    color: "#111",
    lineHeight: 1.1,
  },
  formSubtitle: {
    fontFamily: "'Nunito', sans-serif",
    fontSize: "12px",
    fontWeight: 600,
    color: "#999",
    marginTop: "-4px",
  },
  fields: {
    display: "flex",
    flexDirection: "column",
    gap: "9px",
  },
  inputWrap: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "#FAFAFA",
    border: "1.5px solid #E5E7EB",
    borderRadius: "12px",
    padding: "10px 14px",
    transition: "border-color 0.2s, box-shadow 0.2s",
  },
  inputIcon: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
  },
  input: {
    border: "none",
    background: "transparent",
    outline: "none",
    fontFamily: "'Nunito', sans-serif",
    fontWeight: 700,
    fontSize: "14px",
    color: "#111",
    flex: 1,
    width: "100%",
  },
  startBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    background: "#D1D5DB",
    color: "#fff",
    border: "none",
    borderRadius: "14px",
    padding: "13px 20px",
    fontSize: "15px",
    fontWeight: 800,
    fontFamily: "'Nunito', sans-serif",
    cursor: "not-allowed",
    transition: "background 0.25s, transform 0.15s",
    marginTop: "2px",
    letterSpacing: "0.01em",
  },
  startBtnActive: {
    background: "linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)",
    cursor: "pointer",
    boxShadow: "0 4px 16px rgba(245,158,11,0.35)",
  },
  errorMsg: {
    fontFamily: "'Nunito', sans-serif",
    fontSize: "12px",
    color: "#f87171",
    fontWeight: 700,
    textAlign: "center",
    marginTop: "-4px",
  },
};